import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList,
  Clock,
  CheckCircle2,
  ArrowRight,
  FileText,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useEnterprise } from "@/contexts/EnterpriseContext";
import {
  getEnterpriseDashboard,
  type DashboardActionItem,
  type DashboardEvent,
} from "@/services/enterpriseRequestsService";

const STATUS_BADGE_VARIANT: Record<string, StatusBadgeVariant> = {
  pending: "warning",
  in_review: "info",
  awaiting_action: "info",
  approved: "success",
  rejected: "softDanger",
  completed: "success",
  cancelled: "softDanger",
  needs_info: "danger",
  awaiting_child: "info",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  in_review: "En revision",
  awaiting_action: "Accion requerida",
  approved: "Aprobada",
  rejected: "Rechazada",
  completed: "Completada",
  cancelled: "Cancelada",
  needs_info: "Info. solicitada",
  awaiting_child: "Esperando sub-solicitud",
};

const EVENT_LABELS: Record<string, string> = {
  created: "Nueva solicitud",
  step_completed: "Paso completado",
  status_changed: "Estado actualizado",
  info_requested: "Info. solicitada",
  info_reply: "Respuesta recibida",
  rejected: "Solicitud rechazada",
  cancelled: "Solicitud cancelada",
  child_linked: "Sub-solicitud vinculada",
  automation_failed: "Automatizacion fallida",
};

const EVENT_DOT_COLOR: Record<string, string> = {
  created: "bg-blue-500",
  status_changed: "bg-amber-500",
  info_requested: "bg-orange-500",
  info_reply: "bg-green-500",
  rejected: "bg-red-500",
  cancelled: "bg-red-400",
  step_completed: "bg-green-500",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  return `hace ${days}d`;
}

export function EnterpriseDashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const { enterprise, subdomain } = useEnterprise();
  const navigate = useNavigate();

  const dashboardQuery = useQuery({
    queryKey: ["enterprise-dashboard", subdomain],
    queryFn: () => getEnterpriseDashboard(subdomain),
    refetchInterval: 30_000,
  });

  const data = dashboardQuery.data;

  const statCards = useMemo(() => {
    if (!data) return [];
    const s = data.stats;
    return [
      {
        label: "Requieren accion",
        value: data.actionNeeded.length,
        icon: Clock,
        color: "text-amber-600",
        bg: "bg-amber-50 dark:bg-amber-950/30",
      },
      {
        label: "En revision",
        value: (s.in_review ?? 0) + (s.needs_info ?? 0),
        icon: ClipboardList,
        color: "text-blue-600",
        bg: "bg-blue-50 dark:bg-blue-950/30",
      },
      {
        label: "Completadas",
        value: (s.approved ?? 0) + (s.completed ?? 0),
        icon: CheckCircle2,
        color: "text-green-600",
        bg: "bg-green-50 dark:bg-green-950/30",
      },
      {
        label: "Total",
        value: s.total ?? 0,
        icon: FileText,
        color: "text-foreground",
        bg: "bg-muted/50",
      },
    ];
  }, [data]);

  if (dashboardQuery.isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-8">
      <h1 className="text-2xl font-semibold text-foreground">
        {t("enterpriseStaff.dashboard.welcome", {
          name: enterprise?.name ?? "",
        })}
      </h1>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.label} className="shadow-sm">
            <CardContent className="flex items-center gap-4 py-4">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${card.bg}`}>
                <card.icon className={`h-5 w-5 ${card.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{card.value}</p>
                <p className="text-xs text-muted-foreground">{card.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Action needed — main section */}
        <div className="lg:col-span-3">
          <Card className="shadow-sm">
            <CardHeader className="flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">Solicitudes que requieren tu accion</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/requests")}
                className="text-xs"
              >
                Ver todas
                <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </CardHeader>
            <CardContent>
              {data && data.actionNeeded.length > 0 ? (
                <div className="divide-y divide-border">
                  {data.actionNeeded.map((item) => (
                    <ActionNeededRow
                      key={item.id}
                      item={item}
                      onClick={() => navigate(`/requests/${item.id}`)}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <CheckCircle2 className="mb-2 h-8 w-8 text-green-500" />
                  <p className="text-sm font-medium text-foreground">Todo al dia</p>
                  <p className="text-xs text-muted-foreground">No hay solicitudes pendientes de accion.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent activity */}
        <div className="lg:col-span-2">
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Actividad reciente</CardTitle>
            </CardHeader>
            <CardContent>
              {data && data.recentEvents.length > 0 ? (
                <div className="space-y-3">
                  {data.recentEvents.map((event) => (
                    <RecentEventRow
                      key={event.id}
                      event={event}
                      onClick={() => navigate(`/requests/${event.requestId}`)}
                    />
                  ))}
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Sin actividad reciente.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ActionNeededRow({
  item,
  onClick,
}: {
  item: DashboardActionItem;
  onClick: () => void;
}): JSX.Element {
  const nameParts = item.userName.split(" ");
  const firstName = nameParts[0] ?? null;
  const lastName = nameParts.slice(1).join(" ") || null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-muted/50 first:pt-0 last:pb-0 rounded-md px-1 -mx-1"
    >
      <UserAvatar firstName={firstName} lastName={lastName} email={item.userEmail} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground truncate">{item.userName}</p>
          <StatusBadge variant={STATUS_BADGE_VARIANT[item.status] ?? "info"}>
            {STATUS_LABELS[item.status] ?? item.status}
          </StatusBadge>
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {item.productName}
          {item.currentStepLabel && <> &middot; {item.currentStepLabel}</>}
        </p>
      </div>
      <span className="shrink-0 text-[10px] text-muted-foreground">
        {timeAgo(item.createdAt)}
      </span>
    </button>
  );
}

function RecentEventRow({
  event,
  onClick,
}: {
  event: DashboardEvent;
  onClick: () => void;
}): JSX.Element {
  const eventLabel = EVENT_LABELS[event.type] ?? event.type;
  const detail =
    (event.data.nodeLabel as string | undefined) ??
    (event.data.message as string | undefined) ??
    null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-2 rounded-md px-1 -mx-1 py-1.5 text-left transition-colors hover:bg-muted/50"
    >
      <div className={`mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full ${EVENT_DOT_COLOR[event.type] ?? "bg-muted-foreground"}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-foreground">{eventLabel}</p>
        {detail && (
          <p className="text-[11px] text-muted-foreground truncate">{detail}</p>
        )}
        <p className="text-[10px] text-muted-foreground">
          {event.requestUserName && <>{event.requestUserName} &middot; </>}
          {event.requestProductName && <>{event.requestProductName} &middot; </>}
          {timeAgo(event.createdAt)}
        </p>
      </div>
    </button>
  );
}
